import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Switch, Typography, useTheme } from '@mui/material';
import { Button } from '@components/buttons/Button';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { useGetScimToken, useDeleteScimToken } from '@queryHooks/useScimToken';
import { useScimTokenToggle } from './useScimTokenToggle';

export const ScimTokenDetailPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const tenantId = params.tenantId as string;
  const tokenId = params.tokenId as string;

  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  const { data: token } = useGetScimToken(tenantId, tokenId);
  const { mutate: deleteToken } = useDeleteScimToken();
  const { handleToggleStatus } = useScimTokenToggle(tenantId, tokenId);

  const handleGoBack = () => {
    navigate('../scim-tokens');
  };

  const handleDelete = () => {
    alertStore.open({
      type: 'error',
      title: t('ScimToken.delete.title'),
      message: t('ScimToken.delete.message'),
      useCancel: true,
      confirmName: t('ScimToken.delete.confirm'),
      onConfirm: () => {
        deleteToken(
          { tenantId, tokenId },
          {
            onSuccess: () => {
              toastStore.open(t('ScimToken.delete.success'), 'bottom', 'center');
              navigate('..');
            },
            onError: () => {
              toastStore.open(t('ScimToken.delete.fail'), 'bottom', 'center');
            },
          },
        );
      },
    });
  };

  if (!token) return null;

  return (
    <Container>
      <GoBackHeader>
        <GoBackButton onClick={handleGoBack}>
          <ChevronIcon width={24} height={24} rotateNum={-90} color={theme.colors['ic-gray-main']} />
        </GoBackButton>
        <HeaderTitle>{token.description}</HeaderTitle>
      </GoBackHeader>

      <DetailWrapper>
        <InfoRow>
          <InfoLabel>{t('ScimToken.detail.description')}</InfoLabel>
          <InfoValue>{token.description}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('ScimToken.detail.status')}</InfoLabel>
          <StatusRow>
            <Switch checked={token.active} onChange={(_, checked) => handleToggleStatus(checked)} />
            <StatusBadge className={token.statusColor}>{token.statusLabel}</StatusBadge>
          </StatusRow>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('ScimToken.detail.expiresAt')}</InfoLabel>
          <InfoValue>{token.expiresAt || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('ScimToken.detail.createdDate')}</InfoLabel>
          <InfoValue>{token.createdDate || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('ScimToken.detail.lastUsedAt')}</InfoLabel>
          <InfoValue>{token.lastUsedAt || '-'}</InfoValue>
        </InfoRow>
      </DetailWrapper>

      <ActionBox>
        <Button variant="outline" size="small" paddingHorizontal={16} onClick={handleDelete}>
          <DeleteText>{t('ScimToken.button.delete')}</DeleteText>
        </Button>
      </ActionBox>
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  padding: 42px 32px 32px 32px;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
`;

const GoBackHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
`;

const GoBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline5}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const DetailWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 28px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
`;

const InfoRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 32px;
`;

const InfoLabel = styled(Typography)`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  min-width: 160px;
` as typeof Typography;

const InfoValue = styled(Typography)`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const StatusRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled(Box)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0px 5px;
  border-radius: 2px;
  ${fonts.Caption4};

  &.green {
    color: ${({ theme }) => theme.colors['text-green-dark']};
    background: ${({ theme }) => theme.colors['bg-green-lighter']};
    border: 0.5px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  }

  &.gray {
    color: ${({ theme }) => theme.colors['text-gray-sub-darker']};
    background: ${({ theme }) => theme.colors['bg-gray-light']};
    border: 0.5px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  }

  &.red {
    color: ${({ theme }) => theme.colors['text-red-light']};
    background: ${({ theme }) => theme.colors['bg-red-lighter']};
    border: 0.5px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  }
`;

const ActionBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const DeleteText = styled.span`
  color: ${({ theme }) => theme.colors['text-red-light']};
`;
